/**
 * Active-tenant resolution for the platform SPA.
 *
 * This app serves a single tenant — `nmed` — through several stable application
 * aliases. Other deployed and tenant-first local hosts derive the tenant from
 * their leading label so the backend receives the correct `X-Tenant` value.
 *
 * PR-preview hosts are the exception: their leading label is a build
 * identifier, so they fall back to the default tenant like the local hosts do.
 */

import { detectEnv, DEV_EDGE_LABELS } from './org-site';

const TENANT_ALIASES: Record<string, string> = {
   dev: 'nmed',
   development: 'nmed',
   platform: 'nmed',
   production: 'nmed',
};

const DEFAULT_TENANT = 'nmed';

// A PR preview deploys to `pr-<number>.fathom.preview.apaluma.com`, whose
// leading label identifies the build, not a tenant — sending it as `X-Tenant`
// gets the request rejected by the backend's tenant gate. Pattern kept in sync
// with the backend's `cors_origin_regex` and the hub registry's
// `preview_origin_pattern`.
const PREVIEW_BUILD_LABEL = /^pr-[1-9]\d*$/;

function aliased(label: string): string {
   return label ? (TENANT_ALIASES[label] ?? label) : DEFAULT_TENANT;
}

export function currentTenantSlug(hostname: string = window.location.hostname): string {
   const host = hostname.toLowerCase();
   const labels = host.split('.');
   // Tenant-first local dev host `<tenant>.<ticket>.<edge>.localhost`: the
   // leading label is the tenant. Must precede the bare `.localhost` default
   // below, which would otherwise swallow it.
   if (
      labels.length >= 4
      && labels[labels.length - 1] === 'localhost'
      && DEV_EDGE_LABELS.has(labels[2] ?? '')
   ) {
      return aliased(labels[0] ?? '');
   }
   if (labels[labels.length - 1] === 'localhost') {
      return DEFAULT_TENANT;
   }
   // A tenant-first preview host (an org switch away from the entry point) still
   // leads with its tenant, so only the build label itself falls back.
   if (detectEnv(host) === 'preview' && PREVIEW_BUILD_LABEL.test(labels[0] ?? '')) {
      return DEFAULT_TENANT;
   }
   return aliased(labels[0] ?? '');
}
